import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, Lock, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Dedicated email/password sign-in page used by invitation emails.
 * Strips away every other landing-page distraction so candidates with a
 * temporary password cannot accidentally click "Sign in with Google" or
 * "Apply as Candidate" and end up confused.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();

  const prefilledEmail = (params.get("email") || "").trim();
  const magicToken = (params.get("token") || "").trim();
  const [loginData, setLoginData] = useState({
    email: prefilledEmail,
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [autoLoginState, setAutoLoginState] = useState(magicToken ? "trying" : "idle"); // idle | trying | failed

  useEffect(() => {
    // 1) If candidate clicked an invitation email with a magic token, try to
    //    sign them in automatically — no need to type anything. If the token
    //    has expired or already been used, we fall through to the manual form
    //    (which still has their email pre-filled).
    if (magicToken) {
      (async () => {
        try {
          const r = await axios.post(
            `${API}/auth/magic-link-login`,
            { token: magicToken },
            { withCredentials: true }
          );
          setUser(r.data);
          if (r.data.must_change_password) {
            window.location.replace("/change-password");
          } else if (r.data.role === "candidate") {
            window.location.replace(
              r.data.linked_candidate_id ? "/my-application" : "/apply"
            );
          } else {
            window.location.replace("/dashboard");
          }
        } catch (err) {
          // Magic link expired/used — show manual form with email prefilled
          setAutoLoginState("failed");
          toast.info(
            "Your one-click sign-in link has expired. Please sign in below using the temporary password from your invitation email."
          );
        }
      })();
      return;
    }

    // 2) If they already have a valid session, send them where they belong.
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (r.data) {
          if (r.data.must_change_password) {
            navigate("/change-password", { replace: true });
          } else if (r.data.role === "candidate") {
            navigate(r.data.linked_candidate_id ? "/my-application" : "/apply", { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
        }
      } catch {
        /* not logged in — show form */
      }
    })();
  }, [magicToken, navigate, setUser]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const email = (loginData.email || "").trim().toLowerCase();
    const password = loginData.password || "";
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(r.data);
      if (r.data.must_change_password) {
        window.location.href = "/change-password";
      } else if (r.data.role === "candidate") {
        window.location.href = r.data.linked_candidate_id ? "/my-application" : "/apply";
      } else {
        window.location.href = "/dashboard";
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Login failed. Please double-check your email and temporary password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      {autoLoginState === "trying" ? (
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-700 font-medium">Signing you in...</p>
          <p className="mt-1 text-sm text-slate-500">One moment while we set up your session.</p>
        </div>
      ) : (
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="mb-6 inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to home
        </button>
        <Card className="shadow-xl border-slate-200">
          <CardHeader className="text-center pb-3">
            <div className="flex justify-center mb-4">
              <img
                src="/credentia-ai-logo.png"
                alt="Credentia AI"
                className="h-20 w-auto object-contain"
              />
            </div>
            <CardTitle className="text-2xl">Sign In to Credentia AI</CardTitle>
            <CardDescription className="text-base mt-1">
              Use the email address and temporary password from your invitation email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4" autoComplete="on">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    data-testid="login-email"
                    type="email"
                    autoComplete="username"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="your@email.com"
                    value={loginData.email}
                    onChange={(e) =>
                      setLoginData({ ...loginData, email: e.target.value.trim() })
                    }
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Temporary Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    data-testid="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="Enter your temporary password"
                    value={loginData.password}
                    onChange={(e) =>
                      setLoginData({ ...loginData, password: e.target.value })
                    }
                    className="pl-10 pr-10 h-11"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    data-testid="toggle-password"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Tip: type the password manually rather than copy/pasting (paste can include hidden spaces).
                </p>
              </div>
              <Button
                type="submit"
                className="w-full h-11 text-base"
                disabled={loading}
                data-testid="submit-login"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
            <div className="mt-6 pt-4 border-t border-slate-200 text-center">
              <p className="text-xs text-slate-500">
                Trouble logging in? Reply to your invitation email to request a password reset.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
}
